import { SBEFCustomGroup } from '@shared/construction/sbef-custom-group.model';
import { SbefGroupCode } from '@shared/construction/sbef.model';
import type { SbefCode } from '@shared/construction/sbef.model';
import {
  compressConversionFactors,
  getConversionFactorValue,
  sumConversionFactors,
} from '@shared/helpers/conversion-factors.helpers';
import { foldConservativeDeltasFlat } from '@shared/helpers/conservative-carbon.helpers';
import { isDeactivated } from '@shared/helpers/element.helpers';
import { getVersionContentCoverageFraction } from '@shared/helpers/results/content-coverage.helpers';
import {
  getEPDProductId,
  getProductOverride,
} from '@shared/helpers/product-element.helpers';
import { isElementActiveInProposal } from '@shared/helpers/proposal.helpers';
import {
  forEachElement,
  isElement,
  isProductElement,
  type ElementPath,
} from '@shared/helpers/recursive-element.helpers';
import { getResultsById } from '@shared/helpers/results.helpers';
import {
  getSbefCodeForProductElement,
  getSbefCodeFromElementOrRecipe,
  getSbefGroupCode,
} from '@shared/helpers/sbef.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  ProductElementOverrideTarget,
  type Baseline,
  type IElement,
  type IProductElement,
} from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import {
  CONSERVATIVE_A1_A3_UNIT,
  type Results,
  type ResultsRecord,
} from '@shared/models/unit.model';

const CONSTRUCTION_SITE_SBEF_GROUP = new SBEFCustomGroup({
  sbefCodes: ['102', '103', '104', '105'],
  id: 'A5.2-5',
  description: 'Construction site',
});

type ClimateGkSkTotals = { gk: number; sk: number };

type BoverketClimateBucket =
  'grundlaggning' | 'ovriga_barande' | 'klimatskarm' | 'innervaggar';

export type ClimateResultsGkSk = {
  gk: Results;
  sk: Results;
};

export type SumClimateResultsFromRecordOptions = {
  /**
   * When true, the stored conservative A1–A3 delta ({@link CONSERVATIVE_A1_A3_UNIT}, i.e. the
   * per-product Boverket `ConservativeDataConversionFactor` uplift) is added to generic A1–A3
   * for GK. Records saved before DEV-3411 lack the delta and get no uplift.
   */
  conservative?: boolean;
};

export type BoverketClimateImpactFields = {
  barandekonstruktionsdelarGrundlaggningGK: number;
  barandekonstruktionsdelarGrundlaggningSK: number;
  barandekonstruktionsdelarOvrigaGK: number;
  barandekonstruktionsdelarOvrigaSK: number;
  klimatskarmGK: number;
  klimatskarmSK: number;
  innervaggarGK: number;
  innervaggarSK: number;
  a4TransportGK: number;
  a4TransportSK: number;
  a5ByggspillGK: number;
  a5ByggspillSK: number;
  energiPaverkan: number;
  upprakningAvBeraknadKlimatpaverkan: number;
  tackningsgrad: number;
};

export type BuildBoverketClimateImpactFieldsFromRecordOptions =
  SumClimateResultsFromRecordOptions & {
    proposal?: IProposal;
  };

const emptyClimateResultsGkSk = (): ClimateResultsGkSk => ({ gk: {}, sk: {} });

const emptyGkSkTotals = (): ClimateGkSkTotals => ({ gk: 0, sk: 0 });

const addGkSkTotals = (
  target: ClimateGkSkTotals,
  gk: number,
  sk: number,
): void => {
  target.gk += gk;
  target.sk += sk;
};

const roundKg = (value: number): number => Math.round(value);

const toClimatePhaseResults = (a1a3: number, a4: number, a5: number): Results =>
  compressConversionFactors({
    'co2e_A1-A3': a1a3 || undefined,
    co2e_A4: a4 || undefined,
    co2e_A5: a5 || undefined,
  });

const getClimateBucket = (
  sbef?: SbefCode,
): BoverketClimateBucket | undefined => {
  if (!sbef) return undefined;
  const group = getSbefGroupCode(sbef);
  if (group === SbefGroupCode.HouseSubstructure) return 'grundlaggning';
  if (group === SbefGroupCode.Framework) return 'ovriga_barande';
  // SBEF group 6 — innerväggar (DEV-3318).
  if (group === SbefGroupCode.FrameCompletionRoomForm) return 'innervaggar';
  if (group === SbefGroupCode.OuterRoof || group === SbefGroupCode.Facades) {
    return 'klimatskarm';
  }
  return undefined;
};

const isClimateDeclarationSbefScope = (sbef?: SbefCode): boolean =>
  guards.is.defined(getClimateBucket(sbef));

const isA5ConstructionEnergySbef = (sbef?: SbefCode): boolean =>
  guards.is.defined(sbef) && CONSTRUCTION_SITE_SBEF_GROUP.isPartOfGroup(sbef);

/** True when a parent element in `path` is already an A5.2 energy category row. */
const hasA5ConstructionEnergyAncestor = (path: ElementPath): boolean =>
  path.some((node) => {
    if (!isElement(node) || isProductElement(node)) return false;
    return isA5ConstructionEnergySbef(getSbefCodeFromElementOrRecipe(node));
  });

/** A1–A3 + A4 + A5 byggspill from product results (excludes A5.2 energy). */
const sumCo2WithoutEnergyFromResults = (
  results: Results,
  includeByggspill: boolean,
): number => {
  const a5 = includeByggspill
    ? getConversionFactorValue(results, 'co2e_A5')
    : 0;

  return (
    getConversionFactorValue(results, 'co2e_A1-A3') +
    getConversionFactorValue(results, 'co2e_A4') +
    a5
  );
};

const hasManualTransportOverride = (productElement: IProductElement): boolean =>
  guards.is.defined(
    getProductOverride(
      productElement,
      ProductElementOverrideTarget.TRANSPORT_KM,
    ),
  );

const hasManualWasteOverride = (productElement: IProductElement): boolean =>
  guards.is.defined(
    getProductOverride(
      productElement,
      ProductElementOverrideTarget.WASTE_PERCENT,
    ),
  );

/**
 * Fold the stored A1–A3 conservative delta before GK/SK split. Boverket XML uses A1–A3 uplift
 * only; {@link CONSERVATIVE_A5_UNIT} is intentionally left untouched.
 */
const prepareClimateResultsForGkSkSplit = (
  results: Results,
  conservative: boolean,
): Results =>
  conservative
    ? foldConservativeDeltasFlat(results, {
        deltaKeys: [CONSERVATIVE_A1_A3_UNIT],
      })
    : results;

/**
 * Split pre-calculated product {@link Results} into generisk (GK) and specifik (SK) climate buckets.
 * GK and SK are mutually exclusive per phase: generic/schablon data → GK, EPD → SK for A1–A3;
 * default transport/spill → GK, manual overrides → SK for A4/A5.
 * @param results - Product results; pass {@link prepareClimateResultsForGkSkSplit} output when
 *   conservative A1–A3 uplift is required.
 */
export const splitProductClimateResultsGkSk = (
  productElement: IProductElement,
  results: Results,
): ClimateResultsGkSk => {
  const a1a3 = getConversionFactorValue(results, 'co2e_A1-A3');
  const a4 = getConversionFactorValue(results, 'co2e_A4');
  const a5 = getConversionFactorValue(results, 'co2e_A5');

  let a1gk = 0;
  let a1sk = 0;
  if (a1a3 > 0) {
    if (getEPDProductId(productElement)) {
      // EPD → SK only; schablon/generic Boverket data must not be counted as SK.
      a1sk = a1a3;
    } else {
      // Boverket/co2data.fi/schablon → GK.
      a1gk = a1a3;
    }
  }

  let a4gk = 0;
  let a4sk = 0;
  if (a4 > 0) {
    if (hasManualTransportOverride(productElement)) {
      a4sk = a4;
    } else {
      a4gk = a4;
    }
  }

  let a5gk = 0;
  let a5sk = 0;
  if (a5 > 0) {
    if (hasManualWasteOverride(productElement)) {
      a5sk = a5;
    } else {
      a5gk = a5;
    }
  }

  return {
    gk: toClimatePhaseResults(a1gk, a4gk, a5gk),
    sk: toClimatePhaseResults(a1sk, a4sk, a5sk),
  };
};

/**
 * Sum pre-calculated {@link Results} for all product elements under `element`,
 * split into generisk (GK) and specifik (SK) climate data.
 * Mirrors sumElementConversionFactors in results.helpers but reads from `resultsRecord`.
 */
export const sumElementClimateResultsFromRecord = (
  element: IElement,
  resultsRecord: ResultsRecord,
  options: SumClimateResultsFromRecordOptions = {},
): ClimateResultsGkSk => {
  if (isDeactivated(element)) return emptyClimateResultsGkSk();

  const gkRows: Results[] = [];
  const skRows: Results[] = [];

  forEachElement(element, (el, path) => {
    if (!isProductElement(el) || path.some((e) => isDeactivated(e))) return;
    const split = splitProductClimateResultsGkSk(
      el,
      prepareClimateResultsForGkSkSplit(
        getResultsById(resultsRecord, el.id),
        options.conservative === true,
      ),
    );
    gkRows.push(split.gk);
    skRows.push(split.sk);
  });

  return {
    gk: sumConversionFactors(...gkRows),
    sk: sumConversionFactors(...skRows),
  };
};

/**
 * Build Boverket XML klimat fields from a pre-calculated {@link ResultsRecord}.
 * Uses {@link splitProductClimateResultsGkSk} per product row (GK/SK + conservative A1–A3).
 */
export const buildBoverketClimateImpactFieldsFromRecord = (
  version: Baseline,
  resultsRecord: ResultsRecord,
  {
    proposal,
    conservative = true,
  }: BuildBoverketClimateImpactFieldsFromRecordOptions = {},
): BoverketClimateImpactFields => {
  const buckets: Record<BoverketClimateBucket, ClimateGkSkTotals> = {
    grundlaggning: emptyGkSkTotals(),
    ovriga_barande: emptyGkSkTotals(),
    klimatskarm: emptyGkSkTotals(),
    innervaggar: emptyGkSkTotals(),
  };
  const transport = emptyGkSkTotals();
  const byggspill = emptyGkSkTotals();
  let energiPaverkan = 0;
  let co2WithoutEnergy = 0;

  forEachElement(version, (element, path) => {
    if (!isElementActiveInProposal(proposal, path, element)) return;

    // Energy category rows (SBEF 102–105): co2e_A5 is construction-site energy, not byggspill.
    if (isElement(element) && !isProductElement(element)) {
      const sbef = getSbefCodeFromElementOrRecipe(element);
      // Count only the topmost energy row; child rows (e.g. Diesel/Electricity) duplicate the parent total.
      if (
        isA5ConstructionEnergySbef(sbef) &&
        !hasA5ConstructionEnergyAncestor(path)
      ) {
        energiPaverkan += getConversionFactorValue(
          getResultsById(resultsRecord, element.id),
          'co2e_A5',
        );
      }
      return;
    }

    if (!isProductElement(element)) return;

    const results = getResultsById(resultsRecord, element.id);
    const sbef = getSbefCodeForProductElement(element, path);
    if (!isClimateDeclarationSbefScope(sbef)) return;

    const { gk, sk } = splitProductClimateResultsGkSk(
      element,
      prepareClimateResultsForGkSkSplit(results, conservative),
    );
    const bucket = getClimateBucket(sbef);
    if (bucket) {
      addGkSkTotals(
        buckets[bucket],
        getConversionFactorValue(gk, 'co2e_A1-A3'),
        getConversionFactorValue(sk, 'co2e_A1-A3'),
      );
    }

    addGkSkTotals(
      transport,
      getConversionFactorValue(gk, 'co2e_A4'),
      getConversionFactorValue(sk, 'co2e_A4'),
    );

    // A5.1 spill lives on building products (SBEF 2–6 / 63), not only on SBEF code 101.
    const isConstructionEnergy = isA5ConstructionEnergySbef(sbef);
    if (!isConstructionEnergy) {
      addGkSkTotals(
        byggspill,
        getConversionFactorValue(gk, 'co2e_A5'),
        getConversionFactorValue(sk, 'co2e_A5'),
      );
    }

    co2WithoutEnergy += sumCo2WithoutEnergyFromResults(
      results,
      !isConstructionEnergy,
    );
  });

  const coverageFraction = getVersionContentCoverageFraction(version);
  const tackningsgrad =
    coverageFraction >= 1
      ? 100
      : Math.max(1, Math.round(coverageFraction * 100));

  const upprakningAvBeraknadKlimatpaverkan =
    coverageFraction > 0 && coverageFraction < 1
      ? roundKg(co2WithoutEnergy / coverageFraction)
      : roundKg(co2WithoutEnergy);

  return {
    barandekonstruktionsdelarGrundlaggningGK: roundKg(buckets.grundlaggning.gk),
    barandekonstruktionsdelarGrundlaggningSK: roundKg(buckets.grundlaggning.sk),
    barandekonstruktionsdelarOvrigaGK: roundKg(buckets.ovriga_barande.gk),
    barandekonstruktionsdelarOvrigaSK: roundKg(buckets.ovriga_barande.sk),
    klimatskarmGK: roundKg(buckets.klimatskarm.gk),
    klimatskarmSK: roundKg(buckets.klimatskarm.sk),
    innervaggarGK: roundKg(buckets.innervaggar.gk),
    innervaggarSK: roundKg(buckets.innervaggar.sk),
    a4TransportGK: roundKg(transport.gk),
    a4TransportSK: roundKg(transport.sk),
    a5ByggspillGK: roundKg(byggspill.gk),
    a5ByggspillSK: roundKg(byggspill.sk),
    energiPaverkan: roundKg(energiPaverkan),
    upprakningAvBeraknadKlimatpaverkan,
    tackningsgrad,
  };
};
